/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import { useCallback, useRef } from "react";
import { Portal } from "react-portal";
import { usePopper } from "react-popper";

import { useClickOutside, useScrollLock, usePressEsc } from "@smartrent/hooks";

import type { PositionAwarePortalProps, Placement } from "@smartrent/shared-ui";

type Offsets = { placement: Placement; [key: string]: unknown };
type OffsetFn = (config: Offsets) => [number, number];

export function PositionAwarePortal({
  targetRef,
  offset = { skidding: 0, distance: 0 },
  placement = "bottom",
  flip = true,
  visible,
  onClose,
  backdrop = false,
  scrollLockOnVisible = false,
  closeOnOutsideClick = true,
  backdropStyle,
  captureEvent = true,
  children,
}: PositionAwarePortalProps) {
  const popperRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  const offsetOption = useCallback<OffsetFn>(
    ({ placement }) => {
      if (typeof offset === "function") {
        const { skidding, distance } = offset(placement);
        return [skidding, distance];
      }

      return [offset.skidding, offset.distance];
    },
    [offset]
  );

  useScrollLock(targetRef, visible && scrollLockOnVisible);
  useClickOutside({
    refs: backdrop ? [backdropRef] : [targetRef, popperRef],
    captureEvent: false,
    onClose,
    visible: visible && closeOnOutsideClick,
  });
  usePressEsc({
    onClose,
    visible: visible && closeOnOutsideClick,
  });

  const { styles, attributes } = usePopper(
    targetRef.current as any as Element,
    popperRef.current,
    {
      placement,
      modifiers: [
        {
          name: "offset",
          enabled: true,
          options: {
            offset: offsetOption,
          },
        },
        {
          name: "flip",
          enabled: flip,
        },
      ],
    }
  );

  return (
    <Portal>
      {backdrop && visible ? (
        <div
          ref={backdropRef}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            ...backdropStyle,
          }}
          tabIndex={-1}
        />
      ) : null}
      <div
        ref={popperRef}
        style={visible ? styles.popper : { visibility: "hidden" }}
        {...attributes.popper}
      >
        {children}
      </div>
    </Portal>
  );
}
